import React from 'react';

import { VerificationRequestForReportItemFragment, VerificationRequestWhere } from 'src/api';
import { Table, TableColumn, TableTopAction } from 'src/components/table';
import { useFilter, useFilterStorage, useSearch } from 'src/hooks/common';
import {
  useVerificationRequestsReportList,
  useVerificationRequestsTableRowActions,
} from 'src/hooks/reports';

import {
  buildVerificationRequestsReportWhere,
  getVerificationRequestsReportTableFilters,
} from '../reportFilters';

import { VerificationRequestsReportExportButton } from './VerificationRequestsReportExportButton';

interface VerificationRequestsInnerTableProps {
  columns: Array<TableColumn<VerificationRequestForReportItemFragment>>;
  filter?: VerificationRequestWhere;
}

export const VerificationRequestsInnerTable: React.FC<VerificationRequestsInnerTableProps> = ({
  columns,
  filter,
}) => {
  const reportFilters = React.useMemo(() => getVerificationRequestsReportTableFilters(), []);

  // Use the *applied* filter (committed on "Apply filters"), not the in-progress
  // `tableFilter`, which TableFilters resets on apply. This also drives the
  // active-filter badge on the filter icon, which is keyed on the applied filter.
  const { filter: appliedFilter } = useFilter();
  const filterWhere = React.useMemo(
    () => buildVerificationRequestsReportWhere(appliedFilter),
    [appliedFilter],
  );

  // The incoming `filter` (inbox item type scoping) and the report filters
  // (createdAt / type / status) use disjoint top-level keys, so a merge is safe.
  const combinedFilter = React.useMemo<VerificationRequestWhere>(
    () => ({ ...filter, ...filterWhere }),
    [filter, filterWhere],
  );

  const { verificationRequestsCount, verificationRequests, loading } =
    useVerificationRequestsReportList(combinedFilter);

  const { setSearchValue, searchValue } = useSearch();
  const rowActions = useVerificationRequestsTableRowActions();
  const { pagination, setSortOption, sortQueryVariable } = useFilterStorage();

  const topActions = React.useMemo<TableTopAction[]>(
    () => [
      {
        actionName: 'Export',
        render: () => (
          <VerificationRequestsReportExportButton
            key="export"
            where={combinedFilter}
            orderBy={sortQueryVariable}
            disabled={loading}
          />
        ),
      },
    ],
    [combinedFilter, sortQueryVariable, loading],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect must not depend on "pagination" here
  React.useEffect(() => {
    pagination.resetPage();
  }, [searchValue]);

  return (
    <Table
      dataSource={verificationRequests}
      rowCount={verificationRequestsCount}
      columns={columns}
      rowActions={rowActions}
      pagination={pagination}
      onSearch={setSearchValue}
      onSort={setSortOption}
      loading={loading}
      filters={reportFilters}
      topActions={topActions}
      isTrackFilter
    />
  );
};
